import React from 'react';

import { EmptyFieldValueRenderer } from '@/components/molecules/EmptyFieldValueRenderer';
import { FieldValue } from '@/components/molecules/FieldValue';
import { useDataContext } from '@/hooks/useDataContext';
import { Translatable, useTranslator } from '@/hooks/useTranslator';
import { SelectField, SelectItem, ValueType } from '@/types/fields';

interface Props {
  readonly value: ValueType;
  readonly field: SelectField<unknown>;
}
export const SelectFieldValueRenderer: React.FC<Props> = (props: Props): React.ReactElement => {
  const { value, field } = props;
  const { source } = field;

  const [items, setItems] = React.useState<readonly SelectItem[]>([]);

  const data = useDataContext();
  const tr = useTranslator();

  const fetchAndUpdateItems = React.useCallback(async (): Promise<void> => {
    if ('resolve' in source && typeof source.resolve === 'function') {
      const { dependencies } = source;

      try {
        const items = await source.resolve(
          ...dependencies.map((dependency: string): unknown => data[dependency]),
        );
        setItems(items);
      } catch (error: unknown) {
        console.debug(error);
      }
    } else if (Array.isArray(source)) {
      setItems(source);
    }
  }, [data, source]);

  React.useEffect((): void => {
    void fetchAndUpdateItems();
  }, [fetchAndUpdateItems]);

  if (!field.multiple) {
    const selectedItem = items.find((item: SelectItem): boolean => item.value === value);
    if (selectedItem) {
      return <FieldValue value={tr(selectedItem.label)} />;
    }

    return <EmptyFieldValueRenderer />;
  } else if (Array.isArray(source) && field.multiple) {
    if (Array.isArray(value)) {
      const mapped = source.reduce(
        (
          mapped: Record<string, string | Translatable>,
          item: SelectItem,
        ): Record<string, string | Translatable> => {
          return { ...mapped, [item.value]: tr(item.label) };
        },
        {},
      );
      const valueAsText = value
        .map((itemValue: string): string => mapped[itemValue] ?? 'Unknown')
        .join(', ');

      return <FieldValue value={valueAsText} />;
    } else {
      console.debug('multiple valued select has a non-array value');
      return <EmptyFieldValueRenderer />;
    }
  } else {
    console.debug('unknown select type');
    // TODO: fetch here? this is tricky :/
    return <EmptyFieldValueRenderer />;
  }
};
